import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@alphabait/ui' // chart'
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts'
import { useTranslation } from 'react-i18next'
import { BaseQuestionRendererProps } from './types'
import { getChartColors, CHART_ACTIVE_COLOR } from '../utils/colors'
import { PieSectorDataItem } from 'recharts/types/polar/Pie'
import { ChartWrapper, ChartEmptyState } from './shared'

export function DropdownPie({ question }: BaseQuestionRendererProps) {
  const { t } = useTranslation()
  const buckets = question.aggregates?.buckets || []
  const totalResponses = question.totalRespondents || 0

  // Collapse categories if more than 8
  let chartData = buckets.slice()
  if (buckets.length > 8) {
    const topBuckets = buckets.slice(0, 7)
    const otherCount = buckets
      .slice(7)
      .reduce((sum, bucket) => sum + bucket.count, 0)
    chartData = [
      ...topBuckets,
      { label: t('dashboard.charts.other'), value: 'other', count: otherCount },
    ]
  }

  // Add percentage calculation
  const dataWithPercentages = chartData.map((item) => ({
    ...item,
    percentage:
      totalResponses > 0
        ? ((item.count / totalResponses) * 100).toFixed(1)
        : '0',
  }))

  // Find the index of the item with the biggest count
  const maxIndex = dataWithPercentages.reduce(
    (maxIdx, item, idx, arr) => (item.count > arr[maxIdx].count ? idx : maxIdx),
    0,
  )

  const colors = getChartColors()
  const chartConfig: ChartConfig = chartData.reduce((config, item, index) => {
    config[item.value || item.label] = {
      label: item.label,
      color: colors[index % colors.length],
    }
    return config
  }, {} as ChartConfig)

  if (buckets.length === 0) {
    return (
      <ChartWrapper
        title={question.label}
        description={t('dashboard.charts.response_distribution')}
      >
        <ChartEmptyState message={t('dashboard.charts.no_responses_yet')} />
      </ChartWrapper>
    )
  }

  return (
    <ChartWrapper
      title={question.label}
      description={`${t('dashboard.charts.response_distribution')} (${t(
        'dashboard.charts.people_answered',
        { count: totalResponses },
      )})`}
      contentClassName="items-start"
    >
      <div className="flex items-center gap-4 h-[250px]">
        {/* Pie Chart - takes up most of the space */}
        <div className="flex-1 h-full">
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dataWithPercentages}
                  cx="50%"
                  cy="50%"
                  innerRadius="40%"
                  outerRadius="80%"
                  dataKey="count"
                  nameKey="label"
                  activeIndex={maxIndex}
                  activeShape={({
                    outerRadius = 0,
                    ...props
                  }: PieSectorDataItem) => (
                    <Sector
                      {...props}
                      outerRadius={outerRadius + 10}
                      fill={CHART_ACTIVE_COLOR}
                    />
                  )}
                >
                  {dataWithPercentages.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colors[(index - 1 + colors.length) % colors.length]}
                    />
                  ))}
                </Pie>
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  formatter={(value, name) => [
                    `${value} responses (${(
                      (Number(value) / totalResponses) *
                      100
                    ).toFixed(1)}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>

        {/* Legend on the right - fixed width */}
        <div className="flex-shrink-0 space-y-1.5 w-[140px] h-full overflow-y-auto">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            {t('dashboard.charts.legend')}
          </div>
          {dataWithPercentages.map((item, index) => (
            <div key={index} className="flex items-start gap-2 text-xs">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5"
                style={{
                  backgroundColor:
                    index === maxIndex
                      ? CHART_ACTIVE_COLOR
                      : colors[(index - 1 + colors.length) % colors.length],
                }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className="text-wrap break-words leading-tight"
                  title={item.label}
                >
                  {item.label}
                </div>
                <div className="text-muted-foreground font-medium mt-0.5">
                  {item.count} ({item.percentage}%)
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ChartWrapper>
  )
}
